/**
 * 第一批真实 op —— 构造 Command，不碰 DOM（副作用集中在 History.execute）。
 *
 * 关键细节：内联 style 会盖住同名属性。若元素写着 style="fill:#f00"，
 * 我们只设 fill 属性用户会看到「改了没反应」。所以设值前若发现内联样式里有同名项，
 * 一并构造一条 style patch 把它摘掉（**patch 构造阶段零副作用**，DOM 只在 execute 时才变）。
 */
import { pathOf } from '../dom';
import type { Command } from '../history';
import { attrPatch, type Patch } from '../patch';
import { DASH_PATTERN, type DashKind } from '../query/props';
import { styleWithoutProp } from '../style-attr';

export interface StyleOpts {
  /** 连续操作（滑杆拖动）合并成一条 undo 用 */
  mergeKey?: string;
}

/** 通用「呈现属性」写入：属性 + 顺带摘掉会盖住它的内联样式项 */
function setPresentation(
  root: Element,
  els: Iterable<Element>,
  name: string,
  value: string | null,
  label: string,
  mergeKey?: string,
): Command {
  const patches: Patch[] = [];
  for (const el of els) {
    const path = pathOf(el, root);
    if (!path) continue;
    const restyled = styleWithoutProp(el.getAttribute('style'), name);
    if (restyled !== null) {
      const sp = attrPatch(path, 'style', el.getAttribute('style'), restyled);
      if (sp) patches.push(sp);
    }
    const p = attrPatch(path, name, el.getAttribute(name), value);
    if (p) patches.push(p);
  }
  return { label, patches, mergeKey };
}

/** 填充色；传 'none' 表示无填充 */
export const setFill = (
  root: Element,
  els: Iterable<Element>,
  value: string | null,
  opts: StyleOpts = {},
): Command => setPresentation(root, els, 'fill', value, '填充', opts.mergeKey);

export const setStroke = (
  root: Element,
  els: Iterable<Element>,
  value: string | null,
  opts: StyleOpts = {},
): Command => setPresentation(root, els, 'stroke', value, '描边', opts.mergeKey);

export const setStrokeWidth = (
  root: Element,
  els: Iterable<Element>,
  width: number | null,
  opts: StyleOpts = {},
): Command =>
  setPresentation(root, els, 'stroke-width', width === null ? null : String(width), '线宽', opts.mergeKey);

export const setDash = (
  root: Element,
  els: Iterable<Element>,
  kind: DashKind,
  opts: StyleOpts = {},
): Command => setPresentation(root, els, 'stroke-dasharray', DASH_PATTERN[kind], '线型', opts.mergeKey);

export const setOpacity = (
  root: Element,
  els: Iterable<Element>,
  value: number,
  opts: StyleOpts = {},
): Command =>
  setPresentation(
    root,
    els,
    'opacity',
    String(Math.min(1, Math.max(0, Number.isFinite(value) ? value : 1))),
    '不透明度',
    opts.mergeKey,
  );
